import { motion, AnimatePresence } from 'framer-motion'
import { Sparkles, Palette, Wand2, Play, Users, Zap, Heart, Star, Gamepad2, Brain, Rocket, Crown, Target, Lightbulb, Trophy, Wand, Paintbrush, Gem, Compass, Camera, Music, BookOpen, Scissors, Brush, PenTool, Image, Layers, Eye, Sparkle, Wand2 as WandIcon, Home, Settings, PenTool as Pen, Palette as PaletteStep, Star as StarStep, Trophy as TrophyStep, Target as TargetIcon, FileText, Lightbulb as LightbulbIcon, Zap as ZapIcon } from 'lucide-react'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

export default function LandingPage() {
  const { t } = useTranslation()
  
  // Dönen ikonlar için state
  const [currentIcons, setCurrentIcons] = useState([0, 1, 2])
  const [isAnimating, setIsAnimating] = useState(false)
  
  // İkon listesi - farklı kategorilerden ikonlar
  const iconSets = [
    [Sparkles, Crown, Star, Zap, Rocket, Trophy, Wand, Gem, Compass], // Yaratıcılık ve büyü
    [Palette, Heart, Lightbulb, Brain, Target, Paintbrush, Camera, Music, BookOpen, Scissors, Brush, PenTool, Image, Layers, Eye, Sparkle], // Sanat ve düşünce
    [Wand2, Gamepad2, Users, Play, WandIcon], // Oyun ve sosyal
  ]
  
  // Slot makinesi efekti - ikonlar sırayla değişir
  useEffect(() => {
    let currentIndex = 0
    const totalIcons = currentIcons.length
    const animationDuration = 2000 // 2 saniye
    const delayBetweenIcons = 1500 // İkonlar arası 1.5 saniye bekleme
    
    const changeNextIcon = () => {
      setIsAnimating(true)
      
      setCurrentIcons(prev => {
        const newIcons = [...prev]
        const availableIcons = iconSets[currentIndex % iconSets.length]
        newIcons[currentIndex] = Math.floor(Math.random() * availableIcons.length)
        return newIcons
      })
      
      currentIndex = (currentIndex + 1) % totalIcons
      
      // Animasyon tamamlandıktan sonra state'i sıfırla
      setTimeout(() => {
        setIsAnimating(false)
      }, animationDuration)
    }
    
    // İlk değişim
    const initialTimeout = setTimeout(changeNextIcon, 3000)
    
    // Sonraki değişimler için interval
    const interval = setInterval(changeNextIcon, animationDuration + delayBetweenIcons)
    
    return () => {
      clearTimeout(initialTimeout)
      clearInterval(interval)
    }
  }, [])

  const features = [
    {
      icon: <Palette className="w-10 h-10" />,
      title: t('home.features.creative.title'),
      description: t('home.features.creative.desc'),
      color: 'from-violet-500 to-purple-500'
    },
    {
      icon: <Wand2 className="w-10 h-10" />,
      title: t('home.features.aiMagic.title'),
      description: t('home.features.aiMagic.desc'),
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: <Sparkles className="w-10 h-10" />,
      title: t('home.features.contest.title'),
      description: t('home.features.contest.desc'),
      color: 'from-amber-500 to-orange-500'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: t('home.features.onlinePlay.title'),
      description: t('home.features.onlinePlay.desc'),
      color: 'from-emerald-500 to-teal-500'
    }
  ]

  return (
    <div className="space-y-12">
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-center p-4 sm:p-6"
      >
        <div className="flex items-center">
          <div className="relative">
            <img src="/assets/logo.png" alt="Scraible Logo" className="w-12 h-12" />
          </div>
          <h1 className="text-xl font-bold text-gradient">{t('home.brand')}</h1>
        </div>

        <div className="flex items-center space-x-3">
          <LanguageSwitcher variant="menu" />
          <a
            href="https://app.scraible.com/login"
            className="btn-primary flex items-center space-x-2"
          >
            <Play className="w-4 h-4" />
            <span className="text-sm">{t('landing.play')}</span>
          </a>
        </div>
      </motion.header>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center space-y-6 sm:space-y-8 py-8 sm:py-12 px-4"
      >
        <div className="relative">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center justify-center space-x-2 sm:space-x-4 mb-4 sm:mb-6"
          >
            {currentIcons.map((iconIndex, index) => {
              const IconComponent = iconSets[index % iconSets.length][iconIndex]
              const colors = ['text-violet-500', 'text-pink-400', 'text-purple-400']
              const sizes = ['w-10 h-10 sm:w-16 sm:h-16', 'w-10 h-10 sm:w-16 sm:h-16', 'w-10 h-10 sm:w-16 sm:h-16']
              
              return (
                <div key={index} className="relative overflow-hidden flex items-center justify-center" style={{ 
                  height: '80px',
                  maskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)'
                }}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`${index}-${iconIndex}`}
                      initial={{ y: -120, rotateX: -90 }}
                      animate={{ y: 0, rotateX: 0 }}
                      exit={{ y: 120, rotateX: 90 }}
                      transition={{ 
                        duration: 2, 
                        ease: [0.25, 0.46, 0.45, 0.94], // Custom easing for slot machine feel
                        type: "tween"
                      }}
                      className="relative flex items-center justify-center"
                    >
                      <IconComponent className={`${sizes[index]} ${colors[index]}`} />
                    </motion.div>
                  </AnimatePresence>
                </div>
              )
            })}
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-gradient mb-4 sm:mb-6 leading-tight"
          >
            {t('home.heroTitleTop')}
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent animate-pulse">ai</span>
            <span className="text-gradient-gold">{t('home.heroTitleBottom')}</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="relative px-4"
          >
            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed">
              {t('home.heroTagline1')} <span className="text-gradient font-semibold">{t('home.heroTagline2')}</span>! 
              {t('home.heroTagline3')} 
              <span className="text-gradient-emerald font-semibold"> {t('home.heroTagline4')}</span> {t('home.heroTagline5')}
            </p>
            <div className="mt-8">
              <a
                href="https://app.scraible.com/login"
                className="btn-primary inline-flex items-center space-x-2 px-6 py-3 text-base sm:text-lg"
              >
                <Play className="w-5 h-5" />
                <span>{t('landing.playNow')}</span>
              </a>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.8 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 px-4"
      >
        {features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
            className="card-gradient text-center space-y-6 floating-card neon-glow group"
          >
            <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${feature.color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
              <div className="text-white">
                {feature.icon}
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-xl font-bold text-white group-hover:text-gradient transition-all duration-300">
                {feature.title}
              </h3>
              <p className="text-white/70 leading-relaxed">
                {feature.description}
              </p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.4 }}
        className="card-gradient border-cyan-400/30"
      >
        <div className="text-center mb-6 sm:mb-8 px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-gradient mb-3">
            {t('home.howToPlay.title')}
          </h2>
          <p className="text-white/70 text-sm sm:text-base">{t('home.howToPlay.subtitle')}</p>
        </div>

        <div className="mb-10">
          <h3 className="text-xl font-bold text-white mb-6 text-center flex items-center justify-center space-x-2">
            <TargetIcon className="w-6 h-6 text-violet-400" />
            <span>{t('home.howToPlay.modesTitle').replace('🎯 ', '')}</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 px-4">
            <div className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 backdrop-blur-sm p-6 rounded-2xl border border-violet-400/30">
              <h4 className="font-bold text-violet-300 mb-4 text-lg flex items-center space-x-2">
                <FileText className="w-5 h-5" />
                <span>{t('home.howToPlay.classicModeTitle').replace('📝 ', '')}</span>
              </h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-violet-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                  <p className="text-white/80">{t('home.howToPlay.classicSteps.0')}</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-violet-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                  <p className="text-white/80">{t('home.howToPlay.classicSteps.1')}</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-violet-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                  <p className="text-white/80">{t('home.howToPlay.classicSteps.2')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 backdrop-blur-sm p-6 rounded-2xl border border-emerald-400/30">
              <h4 className="font-bold text-emerald-300 mb-4 text-lg flex items-center space-x-2">
                <LightbulbIcon className="w-5 h-5" />
                <span>{t('home.howToPlay.creativeModeTitle').replace('💭 ', '')}</span>
              </h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                  <p className="text-white/80">{t('home.howToPlay.creativeSteps.0')}</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                  <p className="text-white/80">{t('home.howToPlay.creativeSteps.1')}</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                  <p className="text-white/80">{t('home.howToPlay.creativeSteps.2')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-bold text-white mb-6 text-center flex items-center justify-center space-x-2">
            <ZapIcon className="w-6 h-6 text-violet-400" />
            <span>{t('home.howToPlay.stepsTitle').replace('⚡ ', '')}</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { step: 1, icon: <Home className="w-6 h-6" />, title: t('home.howToPlay.steps.0.title'), desc: t('home.howToPlay.steps.0.desc') },
              { step: 2, icon: <Settings className="w-6 h-6" />, title: t('home.howToPlay.steps.1.title'), desc: t('home.howToPlay.steps.1.desc') },
              { step: 3, icon: <Pen className="w-6 h-6" />, title: t('home.howToPlay.steps.2.title'), desc: t('home.howToPlay.steps.2.desc') },
              { step: 4, icon: <PaletteStep className="w-6 h-6" />, title: t('home.howToPlay.steps.3.title'), desc: t('home.howToPlay.steps.3.desc') },
              { step: 5, icon: <StarStep className="w-6 h-6" />, title: t('home.howToPlay.steps.4.title'), desc: t('home.howToPlay.steps.4.desc') },
              { step: 6, icon: <TrophyStep className="w-6 h-6" />, title: t('home.howToPlay.steps.5.title'), desc: t('home.howToPlay.steps.5.desc') }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.6 + index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm p-4 rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-300"
              >
                <div className="flex items-start space-x-3">
                  <div className="text-violet-400 flex-shrink-0 mt-1">{item.icon}</div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="w-5 h-5 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {item.step}
                      </span>
                      <h4 className="font-semibold text-white text-sm">{item.title}</h4>
                    </div>
                    <p className="text-white/70 text-xs">{item.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 2 }}
        className="mt-16 pt-8 border-t border-white/10"
      >
        <div className="text-center px-4">
          <div className="flex items-center justify-center space-x-6 text-sm text-white/60">
            <Link
              to="/privacy-policy"
              className="hover:text-violet-400 transition-colors duration-300 flex items-center space-x-2 group"
            >
              <div className="w-2 h-2 bg-violet-400 rounded-full group-hover:animate-pulse"></div>
              <span>Privacy Policy</span>
            </Link>
          </div>
          <div className="mt-4 text-xs text-white/40">
            © 2024 Scraible. All rights reserved.
          </div>
        </div>
      </motion.footer>
    </div>
  )
}


